// src/telas/servicos/index.tsx

import styles from "./styles.module.css";
import { useNavigate } from "react-router-dom";
import { useContext, useEffect, useState, useMemo } from "react";
import { AuthFetchJson, url } from "../../api";
import Swal from "sweetalert2";
import { ContextRegister } from "../../context";
import { FaClipboard, FaClipboardCheck } from "react-icons/fa6";
import { Menu } from "../menu";
import { HiRefresh } from "react-icons/hi";
import { FaChevronUp, FaChevronDown } from "react-icons/fa";

import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  createColumnHelper,
  SortingState,
} from "@tanstack/react-table";

type Servico = {
  Z1_NUMPV: string;
  Z1_EST: string;
  Z1_MUN: string;
  Z1_BAIRRO: string;
  C5_EMISSAO: string;
  Z1_DTAGEND: string;
  Z1_DTRETOR: string;
  Z1_TURNO: string;
  PRAGAS: { DESCRICAO: string }[];
  VRTOTAL: number;
  Z1_STATUS: string;
  Z1_FILIAL: string;
};

const columnHelper = createColumnHelper<Servico>();

const turno: any = {
  M: "Manhã",
  T: "Tarde",
  N: "Noite",
};

const formaterCurrency = new Intl.NumberFormat("pt-br", {
  currency: "BRL",
  style: "currency",
});

function dateFormater(dataStr: string) {
  if (!dataStr || dataStr === "") return "";
  return new Date(
    `${dataStr.slice(0, 4)}-${dataStr.slice(4, 6)}-${dataStr.slice(6, 8)}T00:00:00`,
  ).toLocaleString("pt-BR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

export function Servicos() {
  const {
    state: { usuario },
    state,
  } = useContext(ContextRegister);

  const navigate = useNavigate();
  const [servicos, setServicos] = useState<Servico[]>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState("");

  async function buscarServicos() {
    Swal.fire({
      title: "Aguarde!",
      html: "Buscando dados dos serviços.",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    AuthFetchJson(`${url}COLETAATENDIMENTO`, {
      method: "POST",
      body: JSON.stringify({
        TIPO: "P",
        PARCEIRO: state.usuario.A2_COD,
        LOJA: state.usuario.A2_LOJA,
        STATU: "1",
      }),
    })
      .then((retorno: any) => {
        if (retorno.sucesso !== "T") {
          Swal.fire({
            position: "top-end",
            icon: "error",
            title: "Erro ao buscar os servicos!" + retorno.mensagem,
            showConfirmButton: false,
            timer: 1500,
          });
          return;
        }
        if (Swal.isLoading()) Swal.close();
        setServicos(retorno.dados);
      })
      .catch((error) => {
        Swal.fire({
          position: "top-end",
          icon: "error",
          title: "Erro ao buscar dados!" + error,
          showConfirmButton: false,
          timer: 1500,
        });
      });
  }

  async function pegarServico(servico: Servico) {
    setServicos((prev) =>
      prev.map((s) =>
        s.Z1_NUMPV === servico.Z1_NUMPV
          ? { ...s, Z1_STATUS: "SELECIONADO" }
          : s,
      ),
    );

    Swal.fire({
      title: "Selecionando Serviço",
      text: "Aguarde enquanto estamos selecionando seu serviço!",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    AuthFetchJson(`${url}INSEREVISITA`, {
      method: "POST",
      body: JSON.stringify({
        DADOS: {
          Z1_PARCEIR: usuario.A2_COD,
          Z1_LOJA: usuario.A2_LOJA,
          Z1_FILIAL: servico.Z1_FILIAL,
          Z1_NUMPV: servico.Z1_NUMPV,
          Z1_DTVISIT: "",
          Z1_HRVISIT: "",
          Z1_TECNICO: "",
          Z1_LOJTEC: "",
        },
      }),
    })
      .then((retorno) => {
        if (retorno.sucesso !== "T") {
          Swal.fire({
            title: "Pegar Serviço",
            text: "Erro ao selecionar serviço!",
            icon: "error",
            timer: 1500,
            showConfirmButton: false,
          });
        } else {
          Swal.fire({
            title: "Avaliação",
            text: "Avaliação realizada com sucesso!",
            icon: "success",
            timer: 1500,
            showConfirmButton: false,
          });
          navigate("/meus-servicos");
        }
      })
      .catch((error) => {
        console.log(error);
        Swal.fire({
          title: "Avaliação",
          text: "Erro ao realizar avaliação!",
          icon: "error",
          timer: 1500,
          showConfirmButton: false,
        });
      })
      .finally(() => {
        if (Swal.isLoading()) Swal.close();
      });
  }

  useEffect(() => {
    buscarServicos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = useMemo(
    () => [
      columnHelper.accessor("Z1_NUMPV", {
        header: "OS",
        size: 90,
      }),
      columnHelper.accessor("Z1_EST", {
        header: "Estado",
        size: 70,
      }),
      columnHelper.accessor("Z1_MUN", {
        header: "Cidade",
      }),
      columnHelper.accessor("Z1_BAIRRO", {
        header: "Bairro",
      }),
      columnHelper.accessor("C5_EMISSAO", {
        header: "Data Emissão",
        cell: (info) => dateFormater(info.getValue()),
        size: 120,
      }),
      columnHelper.accessor("Z1_DTAGEND", {
        header: "Data Agendada",
        cell: (info) => dateFormater(info.getValue()),
        size: 120,
      }),
      columnHelper.accessor("Z1_DTRETOR", {
        header: "Data Retorno",
        cell: (info) => dateFormater(info.getValue()),
        size: 120,
      }),
      columnHelper.accessor("Z1_TURNO", {
        header: "Horário",
        cell: (info) => turno[info.getValue()] ?? "",
        size: 90,
      }),
      columnHelper.display({
        id: "pragas",
        header: "Pragas",
        cell: ({ row }) =>
          row.original.PRAGAS.map((p) => p.DESCRICAO).join(", "),
      }),
      columnHelper.accessor("VRTOTAL", {
        header: "Valor",
        cell: (info) => formaterCurrency.format(info.getValue()),
        size: 100,
      }),
      columnHelper.display({
        id: "acoes",
        header: "Ações",
        size: 70,
        cell: ({ row }) => (
          <div className={styles.actionIcons}>
            {row.original.Z1_STATUS === "SELECIONADO" ? (
              <FaClipboardCheck title="Pegar Serviço" />
            ) : (
              <FaClipboard
                title="Pegar Serviço"
                onClick={() => pegarServico(row.original)}
                style={{ cursor: "pointer" }}
              />
            )}
          </div>
        ),
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const table = useReactTable({
    data: servicos,
    columns,
    state: { sorting, globalFilter },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  return (
    <>
      <Menu />
      <div className={styles.container}>
        <div className={styles.backContainer}>
          <p onClick={() => navigate("/home")}>{"< Voltar"}</p>
          <h2>Serviços</h2>

          <div
            onClick={() => buscarServicos()}
            className={styles.reloadContainer}
          >
            Atualizar
            <HiRefresh
              size="18"
              color="#c1c2c3"
              className={styles.reloadIcon}
            />
          </div>
        </div>

        <div className={styles.content}>
          {/* Toolbar: busca + contador */}
          <div className={styles.tableToolbar}>
            <input
              className={styles.searchInput}
              placeholder="Buscar serviço..."
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
            />
            <span className={styles.countLabel}>
              {table.getFilteredRowModel().rows.length} registro(s)
            </span>
          </div>

          <table className={styles.table}>
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      style={{ width: header.getSize() }}
                      onClick={header.column.getToggleSortingHandler()}
                      className={
                        header.column.getCanSort() ? styles.sortable : ""
                      }
                    >
                      <span className={styles.thInner}>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                        {header.column.getIsSorted() === "asc" && (
                          <FaChevronUp className={styles.sortIcon} />
                        )}
                        {header.column.getIsSorted() === "desc" && (
                          <FaChevronDown className={styles.sortIcon} />
                        )}
                      </span>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className={styles.emptyRow}>
                    Nenhum serviço encontrado.
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
